import { useEffect, useRef, useCallback } from 'react';

export interface MicroConfettiProps {
  /** When true, starts the confetti animation */
  trigger: boolean;
  /** Animation duration in ms (default: 2500) */
  duration?: number;
  /** Confetti colors (default: brand colors) */
  colors?: string[];
  /** Number of particles (default: 40) */
  particleCount?: number;
  /** Callback when animation completes */
  onComplete?: () => void;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  rotation: number;
  rotationSpeed: number;
  color: string;
  size: number;
  opacity: number;
}

const DEFAULT_COLORS = ['#F59E0B', '#10B981', '#3B82F6', '#EC4899', '#8B5CF6'];

/**
 * MicroConfetti - Canvas-based confetti animation
 *
 * Respects prefers-reduced-motion and user preferences.
 * Spawns particles from top-center and animates downward with gravity.
 */
export function MicroConfetti({
  trigger,
  duration = 2500,
  colors = DEFAULT_COLORS,
  particleCount = 40,
  onComplete,
}: MicroConfettiProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number | null>(null);
  const hasTriggeredRef = useRef(false);

  const cleanup = useCallback(() => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!trigger || hasTriggeredRef.current) return;

    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      onComplete?.();
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    hasTriggeredRef.current = true;

    // Set canvas size to viewport
    const updateCanvasSize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    updateCanvasSize();

    // Initialize particles at top-center area
    const centerX = canvas.width / 2;
    const startY = 100;
    const particles: Particle[] = [];

    for (let i = 0; i < particleCount; i++) {
      const angle = Math.PI / 4 + (Math.random() * Math.PI) / 2; // Spread 90 degrees
      const velocity = 4 + Math.random() * 6;

      particles.push({
        x: centerX + (Math.random() - 0.5) * 200,
        y: startY + Math.random() * 50,
        vx: Math.cos(angle) * velocity * (Math.random() > 0.5 ? 1 : -1),
        vy: -Math.sin(angle) * velocity + 2,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.3,
        color: colors[Math.floor(Math.random() * colors.length)],
        size: 6 + Math.random() * 6,
        opacity: 1,
      });
    }

    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Update and draw particles
      particles.forEach((particle) => {
        // Update position
        particle.x += particle.vx;
        particle.y += particle.vy;
        particle.vy += 0.15; // Gravity
        particle.vx *= 0.99; // Air resistance
        particle.rotation += particle.rotationSpeed;

        // Fade out in last 30%
        if (progress > 0.7) {
          particle.opacity = 1 - (progress - 0.7) / 0.3;
        }

        // Draw particle (rectangle confetti)
        ctx.save();
        ctx.translate(particle.x, particle.y);
        ctx.rotate(particle.rotation);
        ctx.globalAlpha = particle.opacity;
        ctx.fillStyle = particle.color;
        ctx.fillRect(-particle.size / 2, -particle.size / 4, particle.size, particle.size / 2);
        ctx.restore();
      });

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        cleanup();
        onComplete?.();
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return cleanup;
  }, [trigger, duration, colors, particleCount, onComplete, cleanup]);

  // Reset trigger state when trigger becomes false
  useEffect(() => {
    if (!trigger) {
      hasTriggeredRef.current = false;
    }
  }, [trigger]);

  // Cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-[100]"
      aria-hidden="true"
    />
  );
}
